export interface ReportSection {
    title: string;
    body: string;
}

export interface ParsedReport {
    intro: string;
    sections: ReportSection[];
}

interface HeaderMatch {
    level: number;
    title: string;
    start: number; // index of the header line
    end: number; // index just past the header text (excl. the trailing newline; bodies are trimmed)
}

const HEADER_REGEX = /^(#{1,6})[ \t]+(.+?)[ \t]*$/gm;

/*
 * Splits a markdown report into collapsible sections. Sections are delimited by the largest header
 * level present (e.g. `###`); smaller sub-headers, lists and paragraphs stay inside the section body
 * so they render normally. Any text before the first top-level header is returned as `intro`.
 * When no headers are present, the whole input is returned as `intro` with no sections.
 */
export function parseReportSections(markdown: string): ParsedReport {
    const source = markdown ?? '';

    const headers: HeaderMatch[] = [];
    HEADER_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null = HEADER_REGEX.exec(source);
    while (match !== null) {
        headers.push({
            level: match[1].length,
            title: match[2].trim(),
            start: match.index,
            end: match.index + match[0].length,
        });
        match = HEADER_REGEX.exec(source);
    }

    if (headers.length === 0) {
        return { intro: source.trim(), sections: [] };
    }

    const topLevel = Math.min(...headers.map(h => h.level));
    const topHeaders = headers.filter(h => h.level === topLevel);

    const intro = source.slice(0, topHeaders[0].start).trim();

    const sections: ReportSection[] = topHeaders.map((header, index) => {
        const bodyStart = header.end;
        const bodyEnd = index + 1 < topHeaders.length ? topHeaders[index + 1].start : source.length;
        return {
            title: header.title,
            body: source.slice(bodyStart, bodyEnd).trim(),
        };
    });

    return { intro, sections };
}
